/* =========================================================================
 * Care Plan Cross-Sell — behavior (framework, no styling)
 * -------------------------------------------------------------------------
 * - Intercepts Add to Cart on eligible products (capture phase, before Focal)
 * - Matches the selected variant's price to a tier and opens the popup
 * - Inventory-aware notice (in stock vs made to order)
 * - Accept adds piece + Care Plan; decline/close adds the piece only
 * - Reuses Focal's mini-cart via cart:refresh / cart-notification:show
 * - Auto-removes the Care Plan if its covered piece is removed from cart
 *
 * NOTE: unstyled scaffold. Test on a dev/preview theme before launch.
 * ========================================================================= */
(function () {
  'use strict';

  var POPUP_ID = 'care-plan-popup';
  var pending = null;      // context awaiting an accept/decline decision
  var syncing = false;     // guard for auto-remove re-entrancy

  /* ---------- helpers ---------- */

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }
  function addUrl() {
    return (window.themeVariables && themeVariables.routes && themeVariables.routes.cartAddUrl) || (root() + 'cart/add');
  }
  function cartUrl() {
    return (window.themeVariables && themeVariables.routes && themeVariables.routes.cartUrl) || (root() + 'cart');
  }
  function cartIsPage() {
    return window.themeVariables && themeVariables.settings &&
      (themeVariables.settings.cartType === 'page' || themeVariables.settings.pageType === 'cart');
  }

  function getConfig() {
    var el = document.querySelector('[data-careplan-config]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // Scoped to the nearest .product-form so multi-product pages resolve correctly.
  function productDataFor(form) {
    var scope = form.closest('.product-form');
    var el = scope && scope.querySelector('[data-careplan-product]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // Source of truth for price + inventory: no 250-variant cap, and the theme
  // rewrites it on variant change so the price is never stale. Falls back to a
  // page-wide id match for forms rendered without a picker.
  function selectedVariantFor(form, variantId) {
    var scope = form.closest('.product-form') || document;
    var el = scope.querySelector('script[data-variant]');
    var v = null;
    if (el) {
      try { v = JSON.parse(el.textContent); } catch (e) { v = null; }
    }
    if (v && String(v.id) === String(variantId)) return v;

    var all = document.querySelectorAll('script[data-variant]');
    for (var i = 0; i < all.length; i++) {
      var candidate;
      try { candidate = JSON.parse(all[i].textContent); } catch (e) { continue; }
      if (candidate && String(candidate.id) === String(variantId)) return candidate;
    }
    return null;
  }

  function isEligible(data, config) {
    if (!config) return false;
    var type = (data.type || '').trim().toLowerCase();
    if (config.eligibleType && type === String(config.eligibleType).trim().toLowerCase()) return true;
    if (config.eligibleCollections && config.eligibleCollections.length && Array.isArray(data.collections)) {
      for (var i = 0; i < data.collections.length; i++) {
        if (config.eligibleCollections.indexOf(data.collections[i]) !== -1) return true;
      }
    }
    return false;
  }

  // price is in cents; tier min/max are in dollars.
  // Ranges are half-open (min <= price < max) so touching bounds like 0-100 and
  // 100-200 don't overlap — $100.00 lands in the 100-200 tier, not 0-100.
  function resolveTier(config, priceCents) {
    if (!config || !config.tiers) return null;
    var dollars = priceCents / 100;
    for (var i = 0; i < config.tiers.length; i++) {
      var t = config.tiers[i];
      if (!t.variantId) continue;
      if (dollars >= t.min && dollars < t.max) return t;
    }
    return null;
  }

  function itemFromForm(form) {
    var fd = new FormData(form);
    var item = { id: fd.get('id'), quantity: parseInt(fd.get('quantity') || '1', 10) || 1 };
    var sp = fd.get('selling_plan');
    if (sp) item.selling_plan = sp;
    var props = {};
    var has = false;
    fd.forEach(function (val, key) {
      var m = key.match(/^properties\[(.+)\]$/);
      if (m) { props[m[1]] = val; has = true; }
    });
    if (has) item.properties = props;
    return item;
  }

  /* ---------- interception ---------- */

  document.addEventListener('submit', onSubmit, true); // capture phase — before Focal

  function onSubmit(e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement) || form.getAttribute('is') !== 'product-form') return;

    var ctx = resolveOffer(form, itemFromForm(form));
    if (!ctx) return; // not eligible / no tier / no popup — let Focal add normally

    // We are taking over this add.
    e.preventDefault();
    e.stopImmediatePropagation();

    openOffer(document.getElementById(POPUP_ID), ctx);
  }

  /**
   * Shared eligibility check. Returns the popup context when this add should be
   * intercepted, or null when it should proceed untouched.
   *
   * `item` is passed in rather than read from the form because the ring size
   * modal may have changed the variant before handing off — the Care Plan tier
   * has to be priced against the size the shopper actually ends up with.
   */
  function resolveOffer(form, item, variantOverride) {
    var popup = document.getElementById(POPUP_ID);
    if (!popup) return null; // no popup on page

    var config = getConfig();
    if (!config) return null;

    var variantId = item && item.id;
    if (!variantId) return null;

    var data = productDataFor(form);
    if (!data || !isEligible(data, config)) return null; // not eligible

    // `script[data-variant]` only ever holds the variant the picker is showing.
    // When an upstream popup switched the variant behind the picker's back, that
    // lookup would miss and the offer would be silently skipped, so the caller
    // passes the variant it switched to.
    var variant = variantOverride || selectedVariantFor(form, variantId);
    if (!variant) return null; // can't read the variant — never block the add

    var tier = resolveTier(config, variant.price);
    if (!tier) return null; // out of range — no popup

    return {
      jewelry: item,
      jewelryVariantId: String(variantId),
      tier: tier,
      inStock: (variant.inventory_quantity || 0) > 0
    };
  }

  /**
   * Public handoff for upstream popups that run before this one (ring size).
   * Returns true when the Care Plan took over the add — the caller must then do
   * nothing further, because this module owns adding to the cart from here.
   * Returns false when the Care Plan does not apply, leaving the add to the caller.
   */
  window.BIYA = window.BIYA || {};
  window.BIYA.carePlan = window.BIYA.carePlan || {};
  window.BIYA.carePlan.offer = function (opts) {
    if (!opts || !opts.form || !opts.item) return false;
    var ctx = resolveOffer(opts.form, opts.item, opts.variant);
    if (!ctx) return false;
    if (typeof opts.onBack === 'function') ctx.onBack = opts.onBack;
    if (typeof opts.onDone === 'function') ctx.onDone = opts.onDone;
    openOffer(document.getElementById(POPUP_ID), ctx);
    return true;
  };

  /* ---------- popup ---------- */

  function openOffer(popup, ctx) {
    pending = ctx;

    var priceEl = popup.querySelector('[data-careplan-price]');
    if (priceEl) priceEl.textContent = ctx.tier.priceLabel || '';

    var notices = popup.querySelectorAll('[data-careplan-notice]');
    for (var i = 0; i < notices.length; i++) {
      var kind = notices[i].getAttribute('data-careplan-notice');
      notices[i].hidden = kind === 'in_stock' ? !ctx.inStock : ctx.inStock;
    }

    var back = popup.querySelector('[data-careplan-back]');
    if (back) back.hidden = !ctx.onBack;

    activateTab('plan', popup);
    popup.open = true; // Focal modal-content opens
  }

  function goBack() {
    if (!pending || typeof pending.onBack !== 'function') return;
    var ctx = pending;
    pending = null;

    var popup = document.getElementById(POPUP_ID);
    if (popup) popup.open = false;

    if (ctx.onBack() === false) addToCart([ctx.jewelry]);
  }

  function activateTab(name, popup) {
    popup = popup || document.getElementById(POPUP_ID);
    if (!popup) return;
    var tabs = popup.querySelectorAll('[data-cpp-tab]');
    var panels = popup.querySelectorAll('[data-cpp-panel]');
    for (var i = 0; i < tabs.length; i++) {
      var selected = tabs[i].getAttribute('data-cpp-tab') === name;
      tabs[i].setAttribute('aria-selected', selected ? 'true' : 'false');
      // Roving tabindex: only the selected tab is in the tab order, so Tab moves
      // out of the tablist rather than through it.
      tabs[i].setAttribute('tabindex', selected ? '0' : '-1');
    }
    for (var j = 0; j < panels.length; j++) {
      panels[j].hidden = panels[j].getAttribute('data-cpp-panel') !== name;
    }
    // CSS hides the Care Plan offer + add button on the compare tab, which has
    // its own column CTAs. The decline stays so there's always a way out.
    popup.setAttribute('data-cpp-active', name);
  }

  // Arrow-key navigation across the tablist, per the APG tabs pattern.
  document.addEventListener('keydown', function (e) {
    var tab = e.target.closest && e.target.closest('[data-cpp-tab]');
    if (!tab) return;

    var key = e.key;
    if (key !== 'ArrowRight' && key !== 'ArrowLeft' && key !== 'Home' && key !== 'End') return;

    var list = tab.closest('[role="tablist"]');
    if (!list) return;

    var tabs = Array.prototype.slice.call(list.querySelectorAll('[data-cpp-tab]'));
    var i = tabs.indexOf(tab);
    if (i === -1) return;

    var next;
    if (key === 'Home') next = tabs[0];
    else if (key === 'End') next = tabs[tabs.length - 1];
    else next = tabs[(i + (key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];

    e.preventDefault();
    activateTab(next.getAttribute('data-cpp-tab'));
    next.focus();
  });

  // Delegated clicks for tabs + accept + decline
  document.addEventListener('click', function (e) {
    var tab = e.target.closest && e.target.closest('[data-cpp-tab]');
    if (tab) { activateTab(tab.getAttribute('data-cpp-tab')); return; }

    if (e.target.closest && e.target.closest('[data-careplan-accept]')) { e.preventDefault(); decide(true); return; }
    if (e.target.closest && e.target.closest('[data-careplan-decline]')) { e.preventDefault(); decide(false); return; }
    if (e.target.closest && e.target.closest('[data-careplan-back]')) { e.preventDefault(); goBack(); return; }

    // "View details" leaves for the Care Plan PDP in this same tab.
    var details = e.target.closest && e.target.closest('[data-careplan-details]');
    if (details) {
      var href = details.getAttribute('href');
      if (!href || !pending) return;

      e.preventDefault();

      if (e.metaKey || e.ctrlKey || e.shiftKey || details.target === '_blank') {
        window.open(href, '_blank', 'noopener');
        return;
      }

      var ctx = pending;
      pending = null;
      if (typeof ctx.onDone === 'function') ctx.onDone();

      var popup = document.getElementById(POPUP_ID);
      if (popup) popup.open = false;

      fetch(addUrl() + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [ctx.jewelry] }),
        keepalive: true
      }).then(go, go);

      function go() { window.location.href = href; }
      return;
    }
  });

  // Closing is locked to the "No, thanks" / "Add Care Plan" buttons.
  // - The X is removed from the markup.
  // - Overlay clicks + ESC are blocked here.
  // - While the popup is open we set `careplan-open` on <html>; CSS uses that to
  //   drop any stacked signup popup (Klaviyo) BEHIND ours, so it can't be clicked
  //   and can't trip Focal's focus trap (which would otherwise close ours).
  // - Safety net: if the popup ever closes without a button, add the piece so the
  //   shopper never loses their add.
  (function lockAndObserve() {
    var popup = document.getElementById(POPUP_ID);
    if (!popup) { document.addEventListener('DOMContentLoaded', lockAndObserve); return; }

    // Block Focal's overlay-click close (delegated on .modal__overlay).
    popup.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.modal__overlay')) e.stopImmediatePropagation();
    }, true);

    document.addEventListener('keydown', function (e) {
      if ((e.key === 'Escape' || e.key === 'Esc') && popup.hasAttribute('open')) {
        e.stopImmediatePropagation();
        e.preventDefault();
        decide(false);
      }
    }, true);

    new MutationObserver(function () {
      var open = popup.hasAttribute('open');
      document.documentElement.classList.toggle('careplan-open', open);
      if (!open && pending) decide(false);
    }).observe(popup, { attributes: true, attributeFilter: ['open'] });
  })();

  // Also hide the Klaviyo teaser/popup while Focal's cart drawer is open — same
  // treatment as our popup. The drawer (#mini-cart) is an OpenableElement, so it
  // carries the `open` attribute; mirror it onto <html> as `cart-open` for CSS.
  (function watchCartDrawer() {
    var drawer = document.getElementById('mini-cart');
    if (!drawer) { document.addEventListener('DOMContentLoaded', watchCartDrawer); return; }
    var sync = function () {
      document.documentElement.classList.toggle('cart-open', drawer.hasAttribute('open'));
    };
    new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ['open'] });
    sync();
  })();

  function decide(accepted) {
    if (!pending) return;
    var ctx = pending;
    pending = null; // clear first so the close observer doesn't double-fire

    var items = [ctx.jewelry];
    if (accepted) {
      items.push({
        id: ctx.tier.variantId,
        quantity: ctx.jewelry.quantity,
        properties: { _covered_variant: ctx.jewelryVariantId }
      });
    }

    var popup = document.getElementById(POPUP_ID);
    if (popup) popup.open = false;

    if (typeof ctx.onDone === 'function') ctx.onDone();

    addToCart(items);
  }

  /* ---------- cart ---------- */

  function addToCart(items) {
    return fetch(addUrl() + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items, sections: ['mini-cart'] })
    }).then(function (res) {
      return res.json().then(function (data) { return { ok: res.ok, data: data }; });
    }).then(function (r) {
      if (!r.ok) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }
      if (cartIsPage()) { window.location.href = root() + 'cart'; return; }
      return fetch(cartUrl() + '.js').then(function (res) { return res.json(); }).then(function (cart) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
        cart.sections = r.data.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart: cart, openMiniCart: true }
        }));
        // Focal's cart-notification expects { status, error }. On a drawer cart
        // with a successful add it intentionally shows no toast (the drawer opens
        // instead) — matching native behavior and avoiding the "undefined" banner.
        document.documentElement.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          detail: { status: 'success', error: '' }
        }));
      });
    }).catch(function () {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    });
  }

  /* ---------- auto-remove ---------- */

  document.documentElement.addEventListener('cart:updated', function (e) {
    var cart = e.detail && e.detail.cart;
    if (cart && !syncing) syncCarePlanLines(cart);
  });

  function syncCarePlanLines(cart) {
    var items = cart.items || [];
    var changes = [];
    items.forEach(function (line) {
      var cov = line.properties && line.properties._covered_variant;
      if (!cov) return;
      var coveredQty = items.reduce(function (sum, it) {
        var isCarePlan = it.properties && it.properties._covered_variant;
        return (!isCarePlan && String(it.variant_id) === String(cov)) ? sum + it.quantity : sum;
      }, 0);
      if (line.quantity > coveredQty) changes.push({ key: line.key, quantity: coveredQty });
    });
    if (!changes.length) return;

    syncing = true;
    var chain = Promise.resolve();
    var last = null;
    changes.forEach(function (ch) {
      chain = chain.then(function () {
        return fetch(root() + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: ch.key, quantity: ch.quantity, sections: ['mini-cart'] })
        }).then(function (res) { return res.json(); }).then(function (c) { last = c; });
      });
    });
    chain.then(function () {
      if (last) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: last } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: last } }));
      }
    }).catch(function () {}).then(function () { syncing = false; });
  }
})();
